import React, { Component } from 'react'
import * as d3 from 'd3'
import moment from 'moment'
import { calcDuration } from '../../util/time_utils'

const PX_PER_HOUR = 50
const TOP_NAV_HEIGHT = 120 // todo: make dynamic
const BOTTOM_MARGIN = 13
const date = (timestamp) => new Date(timestamp)

class CalendarDayview {
    constructor(el, props) {
        // set dimensions
        this.width  = props.width  || 600
        this.height = props.height || 900
        this.margin = props.margin || {left: 54, right: 0, top: 0, bottom: 0}
        this.editTimeEntry = props.editTimeEntry
        this.onDayChange = props.onDayChange

        // bind functions
        this.zoomed = this.zoomed.bind(this)
        this.wheeled = this.wheeled.bind(this)
        this.update = this.update.bind(this)
        this.setDay = this.setDay.bind(this)

        // setup zoom control
        this.zoom = d3.zoom()
            .scaleExtent([1 / 2, 4])
            .on('zoom', this.zoomed)

        // create svg container
        this.svg = d3.select(el).append('svg')
            .attr('width', this.width)
            .attr('height', this.height)
            .attr('class', 'calendar-dayview')

        // render side gutter background box
        this.svg.append('rect')
            .attr('height', this.height)
            .attr('width', this.margin.left)
            .attr('class', 'gutter')
        this.svg.append('line')
            .attr('y1', 0)
            .attr('y2', this.height)
            .attr('x1', this.margin.left+1)
            .attr('x2', this.margin.left+1)
            .attr('class', 'gutter')

        this.svg
            .call(this.zoom)
            .on('wheel.zoom', this.wheeled)
            .on('mousedown.zoom', null)
            .on('dblclick.zoom', null)

        this.drawEntries = this.drawEntries.bind(this)
        this.destroy = this.destroy.bind(this)

        // initialize data
        this.data = props.data || []
        this.tags = props.tags || {sort:[], items: {}}

        // TODO: set time range from props

        const startHour = 8
        const endHour = startHour + this.height / PX_PER_HOUR

        const day = props.day ? new Date(props.day) : moment().startOf('day').toDate()
        this.start = d3.timeMinute.offset(d3.timeHour.offset(day, startHour), -15)
        this.end = d3.timeMinute.offset(d3.timeHour.offset(day, endHour), 15)

        // define vertical time scale
        this.yScale = d3.scaleTime()
            .domain([this.start, this.end])
            .rangeRound([0, this.height])

        // define vertical axis
        this.yAxis = d3.axisLeft(this.yScale)
            // .ticks(d3.timeMinute.every(30))
            .tickFormat((date) => {
                if (date.getHours() === 0 && date.getMinutes() === 0) {
                    return d3.timeFormat('%b %-d')(date)
                }
                if (date.getMinutes() === 0) {
                    return d3.timeFormat('%-I %p')(date)
                }
                return d3.timeFormat('%-I:%M %p')(date)
            })
            // .tickFormat(d3.timeFormat('%-I:%M %p'))
            .tickSize(this.width - this.margin.left)
            .tickPadding(5)

        // create axis container and render axis
        this.gY = this.svg.append('g')
            .attr('class', 'axis y')
            .attr('transform', 'translate(' + props.width + ',0)')
            .call(this.yAxis)

        // create time entries container
        this.boxG = this.svg.append('g').attr('class', 'entries')

        this.update(props)
    }

    update(props) {
        if (props) {
            if (props.day) {
                this.setDay(props.day)
            }
            if (props.data) {
                this.data = props.data
            }
            if (props.tags) {
                this.tags = props.tags
            }
            if (props.editTimeEntry) {
                this.editTimeEntry = props.editTimeEntry
            }
        }
        this.drawEntries(this.data)
    }

    zoomed() {
        this.rescaledY = d3.event.transform.rescaleY(this.yScale)
        this.gY.call(this.yAxis.scale(this.rescaledY))
        this.drawEntries(this.data, this.rescaledY)

        const thisZoomStart = this.rescaledY.domain()[0]
        const chartDay = d3.timeDay.floor(thisZoomStart).getTime()
        if (this.day !== chartDay) {
            this.day = chartDay
            this.onDayChange(this.day)
        }
    }

    wheeled() {
        const event = d3.event,
            t = d3.zoomTransform(this)

        if (d3.event.shiftKey) {
            const k = t.k * Math.pow(2, -event.deltaY * (event.deltaMode ? 120 : 1) / 500)
            this.zoom.scaleBy(this.svg, k)
        } else {
            const __zoom = this.svg.property('__zoom') || {k: 1}
            const currentK = __zoom.k
            const translateY = -event.deltaY * 2 / t.k
            const factoredY = translateY / currentK / 4
            this.zoom.translateBy(this.svg, 0, factoredY)
        }

        event.preventDefault && event.preventDefault()
    }

    setDay(newDay) {
        if (this.day === newDay) {
            return
        }
        this.day = newDay

        // Calculate start and end
        const startHour = 8
        const endHour = startHour + this.height / PX_PER_HOUR
        const day = new Date(newDay)
        this.start = d3.timeMinute.offset(d3.timeHour.offset(day, startHour), -15)
        this.end = d3.timeMinute.offset(d3.timeHour.offset(day, endHour), 15)

        // Create shorter var to refer to scale function
        const y = this.yScale

        this.svg
            .transition().duration(250) // transition over 250 ms
            .call(
                this.zoom.transform,
                // calculate new zoom transform
                d3.zoomIdentity
                    .scale(this.height / (y(this.end) - y(this.start)))
                    .translate(0, -y(this.start))
            )
    }

    drawEntries(_data, scale) {
        if (!scale) {
            scale = this.rescaledY || this.yScale
        }

        // filter data down to data within view
        const [start, end] = scale.domain()
        const data = _data.filter(d => {
            return d.startAt <= end.valueOf() && d.endAt >= start.valueOf()
        })

        const entryMargin = 15

        // time entry containers
        const entries = this.boxG.selectAll('rect.entry')
            .data(data)
        entries.enter()
            .append('rect')
            .merge(entries)
            .on("click", (timeEntry) => {
                this.editTimeEntry(timeEntry.id)
            })
            .attr('class', 'entry')
            .attr('rx', 3)
            .attr('stroke-width', 1)
            .attr('x', d => this.margin.left + entryMargin)
            .attr('y', d => scale(date(d.startAt)))
            .attr('height', d =>
                scale(date(d.endAt)) - scale(date(d.startAt))
            )
            .attr('width', d => this.width - this.margin.left - (entryMargin * 2))
            .attr('cursor', 'pointer')
        entries.exit().remove()

        // first tag
        const entryTags = this.boxG.selectAll('rect.entry-tag')
            .data(data)
        entryTags.enter()
            .append('rect')
            .merge(entryTags)
            .attr('class', 'entry-tag')
            .attr('rx', 3)
            .attr('ry', 3)
            .attr('fill', d => {
                if (d.tags.length === 0) {
                    return '#fff'
                }
                const tagId = d.tags[0]
                const tag = this.tags.items[tagId]
                return tag ? tag.color : '#fff'
            })
            .attr('x', d => this.margin.left + entryMargin + 8 )
            .attr('y', d => scale(date(d.startAt)) + 8)
            .attr('height', 4)
            .attr('width', 24)
            .attr('visibility', d =>
                scale(date(d.endAt)) - scale(date(d.startAt)) > 13
                    ? 'inherit'
                    : 'hidden')
        entryTags.exit().remove()

        // time labels
        const entryTimeLabels = this.boxG.selectAll('text.time')
            .data(data)
        entryTimeLabels.enter()
            .append('text')
            .merge(entryTimeLabels)
            .attr('class', 'time')
            .attr('x', d => this.width - entryMargin - 5 )
            .attr('text-anchor', 'end')
            .attr('y', d => scale(date(d.startAt)) + 5 )
            .attr('dy', '1em')
            .text(d => calcDuration(d) + 'h')
            .attr('visibility', d =>
                scale(date(d.endAt)) - scale(date(d.startAt)) > 16
                    ? 'inherit'
                    : 'hidden')
        entryTimeLabels.exit().remove()

        // descriptions
        const entryDescLabels = this.boxG.selectAll('text.description')
            .data(data)
        entryDescLabels.enter()
            .append('text')
            .merge(entryDescLabels)
            .attr('class', 'description')
            .attr('x', d => this.margin.left + entryMargin + 8 )
            .attr('y', d => scale(date(d.startAt)) )
            .attr('dy', '2.3em')
            .text(d => d.description)
            .attr('visibility', d =>
                scale(date(d.endAt)) - scale(date(d.startAt)) > 32
                    ? 'inherit'
                    : 'hidden')
        entryDescLabels.exit().remove()
    }

    destroy(el) {
        // cleanup chart
        this.svg.selectAll("*").remove()
        d3.select(el).select('svg').remove()
    }

    setHeight(height) {
        this.height = height

        this.svg.attr('height', this.height)
        this.svg.select('rect.zoom-control').attr('height', this.height)
        this.svg.select('rect.gutter').attr('height', this.height)
        this.svg.select('line.gutter').attr('height', this.height)

        this.yScale.rangeRound([0, this.height])
        this.rescaledY && this.rescaledY.rangeRound([0, this.height])

        const scale = this.rescaledY || this.yScale
        this.gY.call(this.yAxis.scale(scale))
        this.drawEntries(this.data, scale)
    }
}

/*
CalendarSidebar Props
- timeEntriesArray
- tags
*/

class CalendarSidebar extends Component {
    constructor(props) {
        super(props)
        this.handleResize = this.handleResize.bind(this)
        this.calcHeight = this.calcHeight.bind(this)
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize)
        if (this.props.width) {
            this.initCalendar(this.props)
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize)
        this.destroyCalendar()
    }

    calcHeight() {
        return window.innerHeight - TOP_NAV_HEIGHT - BOTTOM_MARGIN
    }

    initCalendar(props) {
        if (this.calDayview) return
        this.calDayview = new CalendarDayview(this.container, {
            width: props.width,
            height: this.calcHeight(),
            day: props.day,
            data: props.timeEntriesArray,
            tags: props.tags,
            editTimeEntry: props.editTimeEntry,
            onDayChange: props.onDayChange,
        })
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.width !== 0 && !this.calDayview) {
            this.initCalendar(nextProps)
        }
    }

    destroyCalendar() {
        this.calDayview.destroy(this.container)
    }

    componentWillUpdate(nextProps, nextState) {
        if (!this.calDayview) {
            return
        }
        if (nextProps.timeEntriesArray === this.props.timeEntriesArray
            && nextProps.tags !== this.props.tags
            && nextProps.day !== this.props.day) {
            return
        }

        this.calDayview.update({
            day: nextProps.day,
            data: nextProps.timeEntriesArray,
            tags: nextProps.tags,
            editTimeEntry: nextProps.editTimeEntry,
        })
    }

    handleResize() {
        this.calDayview.setHeight(this.calcHeight())
    }

    render() {
        return (
            <div ref={(div) => { this.container = div }} className='calendar-sidebar'>
            </div>
        )
    }
}

export default CalendarSidebar


// WEBPACK FOOTER //
// ./src/components/layout/calendar_sidebar.jsx